/* eslint-disable react/no-array-index-key */
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Popover, Tag } from 'antd';
import React, { HTMLAttributes, memo, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import CSkuCopy from './SkuCopy';
import { useTradeContext } from './StateContext';
import TradeComment from './TradeComment';
import TradeRefund from './TradeRefund';
import { openExternalURL } from 'utils/openExternalURL';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';
import Copy from './Copy';
import { ProductImages } from './ProductImages';
import { useReturnTypeDisplay } from 'views/Refund/hooks/useReturnTypeDisplay';
import { slice } from 'lodash-es';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { formatNumber } from 'utils/numberFormat';
import { useOrderProductShowPrice } from 'hooks/business/chat/useOrderProduct';
import { usePreOrder } from 'hooks/business/chat/usePreOrder';
import CombinedListingSkus from './CombinedListingSkus';

const Container = styled.section`
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 5px;

  .itme-link-icon {
    display: none;
  }
`;

const SkuCopy = styled(CSkuCopy)`
  > span {
    &:first-of-type {
      flex: 0 1 auto;
      overflow: hidden;
    }
  }
`;
const Content = styled.div`
  display: flex;
  justify-content: space-between;
  > div {
    flex: 1 0 40%;
    overflow: hidden;
  }
  > span {
    flex: 1 0 0;
    overflow: hidden;
  }
`;

const Info = styled.div<{ hit: boolean }>`
  background: ${({ hit }) => (hit ? '#FFE3BC' : 'transparent')};
  [data-color-mode='dark'] & {
    background: ${({ hit }) => (hit ? 'rgba(255, 200, 140, 0.25)' : 'transparent')};
  }
  > * {
    margin-bottom: 5px;
    line-height: 14px;
    font-size: 14px;
  }
  > h5 {
    display: flex;
    align-items: center;
    color: var(--fett-text-primary);
    overflow: hidden;
    > * {
      flex: 0 0 auto;
    }
    > div {
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &:hover {
      cursor: pointer;
      color: var(--fett-scenario-accent-text);
    }
  }
  > div {
    color: var(--fett-text-tertiary);
  }
`;

export const Combine = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  font-size: 12px;
  color: var(--fett-text-secondary);
  background: var(--fett-border-default);
  border-radius: 18px;
  cursor: pointer;
  > span {
    margin-left: 4px;
    font-size: 10px;
  }
`;

export const ItemLink = styled.section`
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  padding: 0 !important;
  line-height: 20px;
  text-align: center;
  background: var(--fett-scenario-accent-strong-bg);
  color: var(--fett-scenario-accent-text);
  border-radius: 2px;
  cursor: pointer;
`;

const Extra = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  > span {
    margin-right: 0;
    line-height: 18px;
    font-size: 14px;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    word-break: break-all;
  }
`;

const Block = `
  height: 24px;
  margin: 10px 0;
  padding: 0 10px;
  background: var(--fett-product-table-stripe);
  border-radius: 2px;
`;

const RefundReason = styled(TradeRefund)`
  ${Block}
`;

const Comment = styled(TradeComment)`
  ${Block}
`;

export interface SimpleCardProps extends HTMLAttributes<HTMLDivElement> {
  product: Conversation.Trade.Product;
  showReturn?: boolean;
  unit: string;
  namePrefix?: React.ReactNode;
  externalShopId: string;
  lazyScrollContainer?: Element | null;
}

export function SimpleCard(
  props: SimpleCardProps & {
    platform: General.Platform;
    region: General.UpperCaseRegion;
    exportMode?: boolean;
    isDetail?: boolean;
  },
) {
  const {
    product,
    unit,
    platform,
    showReturn = false,
    children,
    namePrefix,
    externalShopId,
    exportMode = false,
    isDetail = false,
    lazyScrollContainer,
    ...otherProps
  } = props;
  const {
    itemId,
    itemName: name,
    sellerSku, // sku 货号
    sku,
    itemSku,
    // shopSku,
    // skuName,
    quantity,
    itemPic: pic,
    discountedPrice: price,
    isRefund = false,
    itemUrl,
    skuUrl = itemUrl,
    refundReason = [],
    isPreOrder,
    unitPrice,
    promotionType,
    hit,
    freeGift = false,
    goodsPrepareTime,
    exchangeInfo,
    digitalInfo,
    warehouse,
    combinedListingSkus,
  } = product;
  let url = skuUrl;

  const { t } = useTranslate();
  const returnTypeDisplay = useReturnTypeDisplay();

  const showIsPreOrder = usePreOrder(platform, isPreOrder);
  const skusComp = useMemo(() => {
    // ref: @杜嘉鸿
    // 虾皮平台上不要求sku货号
    // 因此，有sku时不一定也有货号，但是没有sku时一定没有货号
    // 如果没有sku，兜底渲染主商品货号
    if (platform === 'shopee' && !sku && !sellerSku) {
      return (
        <SkuCopy
          exportMode={exportMode}
          width={250}
          sku={itemSku || ''}
          label={`${t('domain.product.field.skuCode')}:`}
        />
      );
    }
    return sku ? <SkuCopy exportMode={exportMode} sku={sku} label="SKU:" /> : null;
  }, [itemSku, platform, sellerSku, t, sku, exportMode]);
  const sellerSkuComp =
    platform === 'shopee' && !sku && !sellerSku ? null : sellerSku ? (
      <SkuCopy
        exportMode={exportMode}
        sku={sellerSku}
        label={`${t('domain.product.field.skuCode')}:`}
      />
    ) : null;

  const digitalInfoComp = useMemo(() => {
    if (platform === 'lazada' && digitalInfo) {
      return (
        <SkuCopy
          exportMode={exportMode}
          sku={digitalInfo}
          label={`${t('domain.product.field.digitalInfo')}:`}
        />
      );
    }
    return null;
  }, [platform, digitalInfo, exportMode, t]);

  const returnType = useMemo(() => {
    const found = refundReason.find((v) => v.returnType);
    return found ? found.returnType : '';
  }, [refundReason]);

  const xiapibuyEnable = useXiapibuyEnable(otherProps.region);

  if (platform === 'shopee') {
    if (xiapibuyEnable && skuUrl && !skuUrl.includes('.xiapibuy.com')) {
      url = `https://${toLowerCase(
        otherProps.region,
      )}.xiapibuy.com/product/${externalShopId}/${itemId}`;
    }
  }

  const showUnitPrice = useOrderProductShowPrice(platform, promotionType);

  const [expand, setExpand] = useState(false);
  const warehouseComponent = warehouse && platform === 'lazada' && (
    <SkuCopy
      exportMode={exportMode}
      sku={warehouse}
      label={`${t('domain.product.field.warehouse')}:`}
    />
  );

  const components = [
    skusComp && <React.Fragment key="skus">{skusComp}</React.Fragment>,
    sellerSkuComp && <React.Fragment key="sellerSku">{sellerSkuComp}</React.Fragment>,
    digitalInfoComp && <React.Fragment key="digitalInfo">{digitalInfoComp}</React.Fragment>,
    goodsPrepareTime > 0 && (
      <div key="goodsPrepareTime" className="!text-[var(--fett-text-secondary)]">
        <label>{t('domain.order.field.processingTime')}: </label>
        <span>
          {t('scene.chatWorkspace.productTab.prepayment.days', { days: goodsPrepareTime })}
        </span>
      </div>
    ),
    warehouseComponent && <React.Fragment key="warehouse">{warehouseComponent}</React.Fragment>,
  ].filter(Boolean) as React.ReactElement[]; // lazada逻辑
  const showLines = showReturn && isRefund && !!returnType ? 3 : 2;
  const showComponents = expand ? components : slice(components, 0, showLines);

  return (
    <Container {...otherProps}>
      <ProductImages images={[pic]} platform={platform} target={lazyScrollContainer} />
      <div className="ml-2 flex-1 overflow-hidden">
        <Content style={returnType ? { opacity: '0.72' } : {}}>
          <Info hit={hit}>
            <h5
              title={name}
              onClick={() => {
                if (url) {
                  openExternalURL(url, '_blank');
                }
              }}
            >
              {namePrefix}
              {freeGift && (
                <Tag color="blue" title={t('domain.product.type.gift')}>
                  {t('domain.product.type.gift')}
                </Tag>
              )}
              {showIsPreOrder && (
                <Tag color="green" title={t('domain.product.type.preOrder')}>
                  {t('domain.product.type.preOrder')}
                </Tag>
              )}
              {exchangeInfo && exchangeInfo.length > 0 ? (
                <Popover
                  content={
                    <>
                      {exchangeInfo.map(({ exchangedSku, exchangedQuantity }, index) => (
                        <div key={index} className="mb-1">
                          <div>
                            {t('scene.chatWorkspace.orderTab.product.label.exchangedSku')}:{' '}
                            {exchangedSku}
                          </div>
                          <div>
                            {t('domain.product.field.quantity')}: {exchangedQuantity}
                          </div>
                        </div>
                      ))}
                    </>
                  }
                >
                  <Tag
                    color="error"
                    title={t('scene.chatWorkspace.orderTab.product.label.exchanged')}
                  >
                    {t('scene.chatWorkspace.orderTab.product.label.exchanged')}
                  </Tag>
                </Popover>
              ) : null}
              <div>{name}</div>
              <Copy
                text={name}
                successMessage={t('common.feedback.copySuccess')}
                failedMessage={t('common.feedback.copyFailed')}
                onClick={(e) => e.stopPropagation()}
              />
            </h5>
            {showComponents}
            {!isDetail && combinedListingSkus?.length ? (
              <Popover
                content={
                  <div style={{ maxWidth: '500px', margin: '-12px -16px' }}>
                    <CombinedListingSkus combinedListingSkus={combinedListingSkus} />
                  </div>
                }
              >
                <Combine>
                  {t('In combined')}
                  <UpOutlined />
                </Combine>
              </Popover>
            ) : null}
          </Info>
          <Extra className="justify-between">
            <Extra>
              <span>{`${unit} ${formatNumber(price, '0,0.00')}`}</span>
              {showUnitPrice ? (
                <div
                  className={css`
                    display: flex;
                    flex-wrap: nowrap;
                    justify-content: flex-end;
                    color: var(--fett-text-tertiary);
                    width: 100%;
                  `}
                >
                  <span
                    className={css`
                      overflow: hidden;
                      white-space: nowrap;
                      text-overflow: ellipsis;
                    `}
                    title={`${unit} ${formatNumber(unitPrice, '0,0.00')}`}
                  >
                    {`[${unit} ${formatNumber(unitPrice, '0,0.00')}`}
                  </span>
                  <span
                    className={css`
                      white-space: nowrap;
                      flex: 0 0 auto;
                    `}
                    title={`x${quantity}`}
                  >{`, x${quantity}]`}</span>
                </div>
              ) : (
                <span>{`x${quantity}`}</span>
              )}
              {showReturn && isRefund && !!returnType ? (
                <Tag color="error" title={returnTypeDisplay(returnType)}>
                  {returnTypeDisplay(returnType)}
                </Tag>
              ) : null}
            </Extra>
            {components.length > showLines && platform === 'lazada' && (
              <Button
                className="!text-[12px] !p-0"
                type="link"
                onClick={() => setExpand((prev) => !prev)}
              >
                {expand
                  ? t('scene.chatWorkspace.orderTab.action.collapse')
                  : t('scene.chatWorkspace.orderTab.action.showMore')}
                {expand ? (
                  <UpOutlined className="!ml-[2px]" />
                ) : (
                  <DownOutlined className="!ml-[2px]" />
                )}
              </Button>
            )}
          </Extra>
        </Content>
        {children}
      </div>
    </Container>
  );
}

export interface TradeProductProps extends Omit<SimpleCardProps, 'children' | 'unit'> {
  conversation: Conversation.Conversation;
  productIndex: number;
  showReturn: boolean;
  showComment?: boolean;
  exportMode?: boolean;
  isDetail?: boolean;
}

function TradeProduct(props: TradeProductProps) {
  const {
    product,
    exportMode = false,
    conversation,
    productIndex,
    showReturn,
    showComment = true,
    isDetail = false,
  } = props;

  const [trade] = useTradeContext();

  const { unit, platform, region, internalTradeId } = trade;
  const { refundReason = [], itemComments = [], combinedListingSkus } = product;

  const simpleCard = (
    <SimpleCard
      platform={platform}
      region={trade.region}
      exportMode={exportMode}
      unit={unit}
      isDetail={isDetail}
      {...props}
    >
      {showReturn ? (
        <RefundReason
          platform={toUpperCase(platform)}
          product={product}
          refundReasons={refundReason}
          productIndex={productIndex}
          region={region}
          storeId={conversation.storeId}
          tradeId={internalTradeId}
        />
      ) : null}
      {showComment
        ? itemComments.slice(-1).map((_, index) => {
            return (
              <Comment
                key={index}
                comments={itemComments}
                platform={platform}
                itemIndex={productIndex}
                commentIndex={index}
              />
            );
          })
        : null}
    </SimpleCard>
  );

  if (!isDetail || !combinedListingSkus?.length) {
    return simpleCard;
  }

  return (
    <div>
      {simpleCard}
      <div style={{ marginTop: '-4px', marginBottom: '6px', background: 'var(--fett-bg-subtle)' }}>
        <CombinedListingSkus combinedListingSkus={combinedListingSkus} />
      </div>
    </div>
  );
}

export default memo(TradeProduct);
